import React from "react";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { ModelMetrics, EdaData } from "../../types";
import { getFeatureStats, getFeatureType } from "./PredictionFormUtils.ts";

interface InputRadarChartProps {
  inputValues: Record<string, string | number>;
  modelData: ModelMetrics | null;
  edaData: EdaData | null;
}

interface RadarDataPoint {
  subject: string; // Feature name
  normalizedValue: number; // Input value normalized to 0-1 range
  fullMark: number; // Always 1, representing the max range
  actualValue: number | string; // Store the original value for tooltip
}

// Helper to prepare data for the radar chart
const prepareRadarData = (
  inputValues: Record<string, string | number>,
  modelData: ModelMetrics | null,
  edaData: EdaData | null,
): RadarDataPoint[] => {
  const data: RadarDataPoint[] = [];
  if (
    !modelData ||
    !modelData.model_metrics ||
    !modelData.model_metrics.model_info ||
    !edaData
  )
    return data;

  const features = modelData.model_metrics.model_info.required_features || [];

  features.forEach((featureName) => {
    const featureType = getFeatureType(modelData, featureName);
    const stats = getFeatureStats(edaData, featureName);
    const currentValue = inputValues[featureName];

    if (
      featureType === "numeric" &&
      stats?.min !== undefined &&
      stats?.max !== undefined &&
      !isNaN(stats.min) &&
      !isNaN(stats.max)
    ) {
      const numValue =
        typeof currentValue === "number"
          ? currentValue
          : parseFloat(currentValue?.toString() ?? "0");
      const min = stats.min;
      const max = stats.max;
      let normalized = 0.5; // Standard für den Fall min === max oder wenn der Wert genau mittig ist

      if (min === max) {
        if (numValue < min) normalized = 0;
        else if (numValue > max)
          normalized = 1; // max ist hier gleich min
        else normalized = 0.5;
      } else {
        // min !== max
        const range = max - min;
        if (range !== 0) {
          // Sollte immer true sein wegen der else Bedingung, aber sicher ist sicher
          normalized = (numValue - min) / range;
        } else {
          // Dieser Fall sollte nicht eintreten, wenn min !== max
          normalized = 0.5;
        }
      }

      normalized = Math.max(0, Math.min(1, normalized));

      data.push({
        subject: featureName,
        normalizedValue: normalized,
        fullMark: 1,
        actualValue: numValue,
      });
    }
  });

  return data;
};

// Custom Tooltip for Radar Chart
const CustomRadarTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload; // Access the data of the hovered point
    const actual = data.actualValue;
    return (
      <div className="custom-tooltip radar-tooltip">
        <p className="label">{`${data.subject}`}</p>
        <p className="intro">{`Aktueller Wert: ${typeof actual === "number" ? actual.toFixed(3) : actual}`}</p>
        <p className="desc">{`(Normalisiert: ${data.normalizedValue.toFixed(3)} / 1.000)`}</p>
      </div>
    );
  }
  return null;
};

export const InputRadarChart: React.FC<InputRadarChartProps> = ({
  inputValues,
  modelData,
  edaData,
}) => {
  const radarData = prepareRadarData(inputValues, modelData, edaData);

  // Radar charts look best with at least 3 axes
  if (radarData.length < 3) {
    return (
      <div className="chart-container card input-radar-chart-container muted">
        <h3>Eingabe Übersicht (Radar)</h3>
        <p>
          Nicht genügend numerische Features mit Min/Max für Radar-Chart
          vorhanden (min. 3 benötigt). Aktuell: {radarData.length}
        </p>
      </div>
    );
  }

  return (
    <div className="chart-container card input-radar-chart-container">
      <h3>Eingabe Übersicht (Radar)</h3>
      <p className="chart-subtitle">
        Zeigt normalisierte Eingabewerte (0=Min, 1=Max aus EDA) für numerische
        Features.
      </p>
      <ResponsiveContainer width="100%" height={400}>
        <RadarChart cx="50%" cy="50%" outerRadius="80%" data={radarData}>
          <PolarGrid />
          <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
          <PolarRadiusAxis
            angle={90}
            domain={[0, 1]}
            tickFormatter={(tick) => tick.toFixed(1)}
          />
          <Radar
            name="Aktuelle Eingabe"
            dataKey="normalizedValue"
            stroke="var(--accent-color)"
            fill="var(--accent-color)"
            fillOpacity={0.6}
          />
          {/* Optional: Radar for the max range (always 1) - might clutter */}
          {/* <Radar 
                        name="Max Range (EDA)" 
                        dataKey="fullMark" 
                        stroke="var(--text-secondary)" 
                        fill="var(--text-secondary)"
                        fillOpacity={0.1}
                        dot={false}
                        activeDot={false}
                    /> */}
          <Tooltip content={<CustomRadarTooltip />} />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
};
